import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';

import {
  CarouselContainer,
  SlideContainer,
  SlideContent,
  SlideImage,
  SlideInfo,
  SlideTitle,
  SlideButton,
  DotsContainer,
  Dot,
  NavigationButton
} from './styles';
import { HeroBannerData } from '../../store/modules/category/types';

interface HeroBannerProps {
  data: HeroBannerData;
}

const HeroBanner: React.FC<HeroBannerProps> = ({ data }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { banners, size } = data;
  const history = useHistory();
  const isMountedRef = useRef(true);

  const handleBannerClick = useCallback((banner: typeof banners[0]) => {
    const [categoryId, subcategoryId] = banner.action?.category_id || [];
    const hasAction = ((categoryId?.length || 0) > 0) ||
                      ((subcategoryId?.length || 0) > 0) ||
                      (banner.action?.externalUrl && banner.action.externalUrl.length > 0);
    const hasButton = !!(banner.action && banner.action.show_button);

    if (hasAction && !hasButton) {
      const isExternalLink = banner.action && banner.action.externalUrl && banner.action.externalUrl.length > 0;
      
      if (isExternalLink && banner.action?.externalUrl) {
        window.open(banner.action.externalUrl, '_blank', 'noopener,noreferrer');
      } else {
        const categoryParam = categoryId ? `?category=${categoryId}` : '';
        const subcategoryParam = subcategoryId ? `${categoryParam ? '&' : '?'}subcategory=${subcategoryId}` : '';
        history.push(`${categoryParam}${subcategoryParam}`);
      }
    }
  }, [history, banners]);

  useEffect(() => {
    isMountedRef.current = true;
    const randomInterval = Math.floor(Math.random() * (10000 - 6000 + 1)) + 6000;
    
    const interval = setInterval(() => {
      if (isMountedRef.current) {
        setCurrentSlide((prev) => (prev + 1) % banners.length);
      }
    }, randomInterval);

    return () => {
      isMountedRef.current = false;
      clearInterval(interval);
    };
  }, [banners.length]);

  if (banners.length === 0) {
    return <></>
  }

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev - 1 + banners.length) % banners.length);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % banners.length);
  };

  return (
    <CarouselContainer size={size}>
      <SlideContainer currentSlide={currentSlide}>
        {banners.map((banners, index) => {
          const [categoryId, subcategoryId] = banners.action?.category_id || [];
          const hasAction = ((categoryId?.length || 0) > 0) ||
                            ((subcategoryId?.length || 0) > 0) ||
                            (banners.action?.externalUrl && banners.action.externalUrl.length > 0);
          const hasButton = !!(banners.action && banners.action.show_button && banners.action.show_button === 1);
          const isExternalLink = banners.action && banners.action.externalUrl && banners.action.externalUrl.length > 0;
          const isClickable = !!(hasAction && !hasButton);
          
          return (
            <SlideContent 
              key={banners.id} 
              active={index === currentSlide}
              clickable={isClickable}
              onClick={() => isClickable && handleBannerClick(banners)}
            >
              <SlideImage src={banners.images.web} alt={banners.title} />
              <SlideInfo size={data.size}>
                <SlideTitle size={size}>{banners.description}</SlideTitle>
                {hasButton && (isExternalLink ? (
                  <SlideButton as="a" href={banners.action?.externalUrl} target="_blank" rel="noopener noreferrer">
                    {banners.action?.button_title}
                  </SlideButton>
                ) : (
                  <SlideButton as={Link} to={`${`?category=${categoryId}`}${subcategoryId ? `&subcategory=${subcategoryId}` : ''}`}>
                    {banners.action?.button_title}
                  </SlideButton>
                ))}
              </SlideInfo>
            </SlideContent>
          );
        })}
      </SlideContainer>

      {banners.length > 1 && (<>
        <NavigationButton position="left" onClick={goToPrevious}>
          ‹
        </NavigationButton>
        <NavigationButton position="right" onClick={goToNext}>
          ›
        </NavigationButton>

        <DotsContainer>
          {banners.map((_, index) => (
            <Dot
              key={index}
              active={index === currentSlide}
              onClick={() => goToSlide(index)}
            />
          ))}
        </DotsContainer>
      </>)}
    </CarouselContainer>
  );
};

export default HeroBanner;
